import React from "react";
import {
    Box,
    Card,
    Chip,
    Grid,
    LinearProgress,
    Typography,
    Stack
} from "@mui/material";
import { StatusAvatar } from "./StatusAvatar";
import {
    CheckCircle as CheckCircleIcon,
    SyncProblem as SyncProblemIcon,
    Sync as SyncIcon,
    MoreHoriz as MoreHorizIcon
} from "@mui/icons-material";
import { formatDate } from "../utils/formatDate";
import { StatusChip } from "./StatusChip";

export const StageGateCard: React.FC<{ name: string; description: string, owner: string; due_date: string; status_display_text: string; status: string, checklist: { completed: number, total: number, percent: number }; approval: { completed: number, total: number, percent: number }; artefacts: { completed: number, total: number, percent: number }; color: string; onOpen: () => void }> = ({
    name,
    description,
    owner,
    due_date,
    status,
    status_display_text,
    checklist,
    approval,
    artefacts,
    onOpen,
    color
}) => {
    return (
        <Card
            sx={{
                p: 2,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                flexWrap: "wrap",
                gap: 2,
            }}
            onClick={onOpen}
        >
            <Grid
                container
                spacing={3}
                justifyContent="flex-end"
                alignItems="center"
                flex={1}
            >
                <Grid size={4.5}>
                    <Box display="flex" alignItems="start" gap={2} flex={1}>
                        <Box mt={1}>
                            <StatusAvatar
                                size="large"
                                bgcolor={
                                    color === 'green'
                                        ? 'success.main'
                                        : color === 'amber'
                                            ? 'warning.main'
                                            : color === 'red'
                                                ? 'error.main'
                                                : 'default'
                                }>
                                {
                                    status === "completed" ?
                                        <CheckCircleIcon /> :
                                        status === "awaiting_approval" || status === "requires_action" || status === "on_hold" || status === "awaiting_decision" ?
                                            <SyncProblemIcon /> :
                                            status === "in_progress" ?
                                                <SyncIcon /> :
                                                <MoreHorizIcon />
                                }
                            </StatusAvatar>
                        </Box>
                        <Box>
                            <Stack direction="row" alignItems="center" spacing={1} gap={0}>
                                <Typography
                                    variant="subtitle2"
                                    color="text.primary"
                                >
                                    {name}
                                </Typography>
                                <StatusChip
                                    label={status_display_text}
                                    size="small"
                                    color={color as 'red' | 'green' | 'amber'}
                                    variant="outlined"
                                />
                            </Stack>
                            <Typography variant="caption" color="text.primary">
                                {description}
                            </Typography>
                            <Stack direction="row" spacing={2} mt={0.5}>
                                <Typography variant="caption" color="text.secondary">
                                    Owner: {owner || 'N/A'}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                    Due: {formatDate(due_date) || 'N/A'}
                                </Typography>
                            </Stack>
                        </Box>
                    </Box>
                </Grid>
                <Grid size={2.5} display="flex" flexDirection="column">
                    <Typography variant="caption" color="text.primary">
                        Gate Checklist
                    </Typography>
                    <Box display={'flex'} justifyContent={'space-between'}>
                        <Typography variant="caption" color="text.secondary">
                            {checklist.completed} of {checklist.total} required items
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                            {checklist.percent} %
                        </Typography>
                    </Box>
                    <LinearProgress
                        variant="determinate"
                        value={checklist.percent}
                        sx={{ mt: 0.5, height: 8, borderRadius: 5 }}
                        color="success"
                    />
                </Grid>
                <Grid size={2.5} display="flex" flexDirection="column">
                    <Typography variant="caption" color="text.primary">
                        Approvals Required
                    </Typography>
                    <Box display={'flex'} justifyContent={'space-between'}>
                        <Typography variant="caption" color="text.secondary">
                            {approval.completed} of {approval.total} required items
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                            {approval.percent} %
                        </Typography>
                    </Box>
                    <LinearProgress
                        variant="determinate"
                        value={approval.percent}
                        sx={{ mt: 0.5, height: 8, borderRadius: 5 }}
                        color="success"
                    />
                </Grid>
                <Grid size={2.5} display="flex" flexDirection="column">
                    <Typography variant="caption" color="text.primary">
                        Artefacts Required
                    </Typography>
                    <Box display={'flex'} justifyContent={'space-between'}>
                        <Typography variant="caption" color="text.secondary">
                            {artefacts.completed} of {artefacts.total} required items
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                            {artefacts.percent} %
                        </Typography>
                    </Box>
                    <LinearProgress
                        variant="determinate"
                        value={artefacts.percent}
                        sx={{ mt: 0.5, height: 8, borderRadius: 5 }}
                        color="success"
                    />
                </Grid>
            </Grid>
        </Card>
    );
};